import * as countries from 'i18n-iso-countries';
import { Match, MatchStatus } from '../../models/match.model';
import { MatchFilterId } from '../sportsbook.models';
import { SportsbookLocalizationContext, resolveAwayTeamLabel, resolveHomeTeamLabel, resolveOutrightLabel, resolveSportLabel } from './sportsbook-localization.utils';
import { normalizeSearchText } from './sportsbook-shared.utils';

export function buildMetaLines(
  match: Match,
  options: {
    isLiveMatch(match: Match): boolean;
    formatKickoffTime(value: string): string;
  }
): readonly string[] {
  if (!options.isLiveMatch(match)) {
    return [options.formatKickoffTime(match.Date)];
  }

  const liveSegments = [
    match.SetScore,
    match.MatchTimeExtended,
    match.RemainingTime
  ]
    .map(value => value == null ? '' : String(value).trim())
    .filter(Boolean);

  if (liveSegments.length) {
    return liveSegments.slice(0, 2);
  }

  return [];
}

export function parseScore(score: string): readonly string[] {
  if (!score) {
    return [];
  }

  const scoreLines = score
    .split(/[-:]/)
    .map(part => part.trim())
    .filter(Boolean);

  return scoreLines.length === 2 ? scoreLines : [];
}

export function matchesFilter(
  match: Match,
  filter: MatchFilterId,
  context: {
    isDisplayableMatch(match: Match): boolean;
    isLiveMatch(match: Match): boolean;
    now?: Date;
  }
): boolean {
  if (!context.isDisplayableMatch(match)) {
    return false;
  }

  if (filter === 'all') {
    return true;
  }

  if (filter === 'highlights') {
    return match.TournamentRating === 'A';
  }

  if (filter === 'live') {
    return context.isLiveMatch(match);
  }

  if (filter === 'outrights') {
    return match.Outright;
  }

  const matchDate = new Date(match.Date);
  const now = context.now ?? new Date();
  const targetDate = new Date(now);
  if (filter === 'tomorrow') {
    targetDate.setDate(now.getDate() + 1);
  }

  return isSameCalendarDate(matchDate, targetDate);
}

export function matchesSearchQuery(
  match: Match,
  queryTokens: readonly string[],
  context: SportsbookLocalizationContext
): boolean {
  if (!queryTokens.length) {
    return true;
  }

  const homeTeam = resolveHomeTeamLabel(match, context.currentLanguage);
  const awayTeam = resolveAwayTeamLabel(match, context.currentLanguage);
  const outrightLabel = match.Outright ? resolveOutrightLabel(match, context.currentLanguage) : '';
  const searchableText = normalizeSearchText([
    resolveSportLabel(match.Sport, context.t),
    match.Tournament,
    match.Category,
    homeTeam,
    awayTeam,
    outrightLabel,
    awayTeam ? `${homeTeam} v ${awayTeam}` : homeTeam
  ].join(' '));

  return queryTokens.every(token => searchableText.includes(token));
}

export function isDisplayableMatch(match: Match): boolean {
  return match.Active && match.MatchStatus !== MatchStatus.ENDED;
}

export function isBettableMatch(match: Match): boolean {
  return isDisplayableMatch(match) && match.BetStatus;
}

export function isLiveMatch(match: Match): boolean {
  if (match.Live) {
    return true;
  }

  switch (match.MatchStatus) {
    case MatchStatus.ERROR:
    case MatchStatus.NOT_STARTED:
    case MatchStatus.ENDED:
    case MatchStatus.WALKOVER:
    case MatchStatus.RETIRED:
    case MatchStatus.ABANDONED:
    case MatchStatus.CANCELED:
    case MatchStatus.POSTPONED:
      return false;
    default:
      return true;
  }
}

function isSameCalendarDate(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

export function formatKickoffTime(value: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value));
}

const GLOBE_ICON = '🌐';

function toFlagEmoji(alpha2CountryCode: string): string {
  return String.fromCodePoint(...alpha2CountryCode.split('').map(char => 127397 + char.charCodeAt(0)));
}

export function resolveCountryFlag(countryCode: string | null): string {
  const normalizedCountryCode = countryCode?.trim().toUpperCase() ?? '';
  const alpha2CountryCode = countries.alpha3ToAlpha2(normalizedCountryCode);

  return alpha2CountryCode ? toFlagEmoji(alpha2CountryCode) : GLOBE_ICON;
}

export function getSelectionOrder(marketKey: string, outrightMarketKey = 'outright-winner'): number {
  switch (marketKey) {
    case outrightMarketKey:
      return 0;
    case 'result':
      return 0;
    case 'totals':
      return 1;
    case 'ggng':
      return 2;
    default:
      if (marketKey.startsWith('detail:')) {
        return 10;
      }

      return 99;
  }
}
